import { useState } from 'react';
import * as S from '../../domain/sceneOps';
import { CHARACTER_STYLES, MAX_ACCESSORIES, type Accessory, type Character, type CharacterProportions } from '../../domain/schemas';
import { createId } from '../../lib/ids';
import { useObservable } from '../../lib/useObservable';
import { CHARACTER_PRESETS, CHARACTER_PRESET_IDS, characterSvg } from '../../scene/characters';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Alert, Button, ConfirmDialog, Panel } from '../ui';
import type { EditFn } from './useSceneEdit';

const sel = 'w-full rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm';

export function CharacterThumb({ character, size = 64 }: { character: Character; size?: number }) {
  return <img alt="" aria-hidden="true" width={size / 2} height={size} src={`data:image/svg+xml;utf8,${encodeURIComponent(characterSvg(character))}`} className="shrink-0" />;
}

const PROPORTIONS: [keyof CharacterProportions, string, number, number][] = [
  ['headSize', 'Head size', 0.12, 0.32],
  ['torsoWidth', 'Torso width', 0.3, 0.7],
  ['torsoHeight', 'Torso height', 0.2, 0.4],
  ['legLength', 'Leg length', 0.2, 0.45],
  ['armLength', 'Arm length', 0.6, 1.3],
  ['limbThickness', 'Limb thickness', 0.06, 0.16],
];

const label = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Character library and editor. Characters are structured data (proportions, parts,
 * colours, accessories); every instance placed in any scene draws from the definition, so
 * edits here update all instances while each instance keeps its own size and position.
 */
export function CharacterStudio({ edit, onPlace }: { edit: EditFn; onPlace: (characterId: string) => void }) {
  const { project } = useProject();
  const { selection } = useServices();
  const selected = useObservable(selection);
  const [deleting, setDeleting] = useState<Character | null>(null);
  const [error, setError] = useState<string | null>(null);
  const current = project.characters.find((c) => c.id === selected.characterId) ?? null;

  const create = (preset: (typeof CHARACTER_PRESET_IDS)[number]) => {
    const id = createId('chr');
    if (edit('New character', (p) => S.createCharacter(p, preset, id))) selection.selectCharacter(id);
  };

  return (
    <Panel title="Character studio" headingLevel={3}>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-2" role="group" aria-label="New character from a preset">
          {CHARACTER_PRESET_IDS.map((id) => (
            <Button key={id} size="sm" onClick={() => create(id)} data-testid={`new-character-${id}`}>
              + {CHARACTER_PRESETS[id].label}
            </Button>
          ))}
        </div>
        {error && <Alert title={error} action={<Button size="sm" variant="ghost" onClick={() => setError(null)}>Dismiss</Button>} />}
        {project.characters.length === 0 ? (
          <p className="text-sm text-zinc-400">No characters yet. Start from a preset above; every part stays editable.</p>
        ) : (
          <ul className="flex gap-2 overflow-x-auto pb-1" aria-label="Character library" data-testid="character-library">
            {project.characters.map((c) => {
              const uses = S.characterUsage(project, c.id).reduce((n, u) => n + u.objectIds.length, 0);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    aria-pressed={c.id === current?.id}
                    onClick={() => selection.selectCharacter(c.id === current?.id ? null : c.id)}
                    className={`flex w-28 flex-col items-center gap-1 rounded-md border p-2 text-xs ${c.id === current?.id ? 'border-accent-400 bg-accent-600/10' : 'border-zinc-800 bg-zinc-900 hover:border-zinc-600'}`}
                    data-testid="character-card"
                  >
                    <CharacterThumb character={c} />
                    <span className="w-full truncate text-center font-medium">{c.name}</span>
                    <span className="text-zinc-400">{uses ? `placed ${uses}×` : 'not placed'}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {current && (
          <CharacterEditor
            key={current.id}
            character={current}
            edit={edit}
            onPlace={() => onPlace(current.id)}
            onDuplicate={() => {
              const id = createId('chr');
              if (edit('Duplicate character', (p) => S.duplicateCharacter(p, current.id, id))) selection.selectCharacter(id);
            }}
            onDelete={() => setDeleting(current)}
          />
        )}
      </div>
      <ConfirmDialog
        open={deleting !== null}
        danger
        title={`Delete “${deleting?.name ?? ''}”?`}
        body={(() => {
          if (!deleting) return '';
          const usage = S.characterUsage(project, deleting.id);
          const count = usage.reduce((n, u) => n + u.objectIds.length, 0);
          return count
            ? `This character is placed ${count} time${count === 1 ? '' : 's'} (${usage.map((u) => u.sceneName).join(', ')}). Deleting it also removes those instances.`
            : 'The character definition is removed. This can be undone with Undo.';
        })()}
        confirmLabel="Delete character"
        onCancel={() => setDeleting(null)}
        onConfirm={() => {
          const c = deleting!;
          setDeleting(null);
          if (edit('Delete character', (p) => S.deleteCharacter(p, c.id, true))) selection.selectCharacter(null);
          else setError('The character could not be deleted.');
        }}
      />
    </Panel>
  );
}

function CharacterEditor({ character: c, edit, onPlace, onDuplicate, onDelete }: { character: Character; edit: EditFn; onPlace: () => void; onDuplicate: () => void; onDelete: () => void }) {
  const set = (labelText: string, patch: S.CharacterPatch, key: string) => edit(labelText, (p) => S.updateCharacter(p, c.id, patch), `${c.id}:${key}`);
  const f = (k: string) => `chr-${c.id}-${k}`;
  const color = (id: string, text: string, value: string, onChange: (v: string) => void) => (
    <div className="flex items-center gap-2">
      <input id={id} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-8 w-10 shrink-0 cursor-pointer rounded border border-zinc-700 bg-transparent" />
      <label htmlFor={id} className="text-xs text-zinc-300">
        {text}
      </label>
    </div>
  );
  const choice = <T extends string>(id: string, text: string, value: T, options: readonly T[], onChange: (v: T) => void) => (
    <div className="flex flex-col gap-0.5">
      <label htmlFor={id} className="text-[11px] text-zinc-400">
        {text}
      </label>
      <select id={id} className={sel} value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o} value={o}>
            {label(o)}
          </option>
        ))}
      </select>
    </div>
  );
  const accessory = (kind: Accessory['kind']) => c.accessories.find((a) => a.kind === kind);
  const toggleAccessory = (kind: Accessory['kind'], on: boolean) => {
    const next = on ? [...c.accessories, { kind, color: '#94a3b8' }] : c.accessories.filter((a) => a.kind !== kind);
    set(on ? 'Add accessory' : 'Remove accessory', { accessories: next }, 'accessories');
  };

  return (
    <div className="@container rounded-md border border-zinc-800 bg-zinc-900/60 p-3" data-testid="character-editor">
      {/* Breakpoints follow the editor's own width: it sits in a narrow column on desktop. */}
      <div className="grid gap-4 @xl:grid-cols-[auto_1fr]">
      <div className="flex flex-col items-center gap-2">
        <CharacterThumb character={c} size={200} />
        <div className="flex flex-wrap justify-center gap-1">
          <Button size="sm" variant="primary" onClick={onPlace} data-testid="place-character">
            Place in scene
          </Button>
          <Button size="sm" onClick={onDuplicate}>
            Duplicate
          </Button>
          <Button size="sm" variant="ghost" className="text-red-300" onClick={onDelete}>
            Delete
          </Button>
        </div>
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-0.5">
          <label htmlFor={f('name')} className="text-[11px] text-zinc-400">
            Name
          </label>
          <input id={f('name')} defaultValue={c.name} maxLength={60} className={sel} onBlur={(e) => e.target.value.trim() && e.target.value !== c.name && set('Rename character', { name: e.target.value }, 'name')} onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()} />
        </div>
        <fieldset className="grid grid-cols-2 gap-x-3 gap-y-1 @md:grid-cols-3">
          <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Proportions</legend>
          {PROPORTIONS.map(([key, text, min, max]) => (
            <div key={key} className="flex flex-col gap-0.5">
              <label htmlFor={f(key)} className="text-[11px] text-zinc-400">
                {text}
              </label>
              <input id={f(key)} type="range" min={min} max={max} step={0.01} value={c.proportions[key]} onChange={(e) => set('Proportions', { proportions: { [key]: Number(e.target.value) } }, key)} />
            </div>
          ))}
        </fieldset>
        <fieldset className="grid grid-cols-2 gap-2 @md:grid-cols-3">
          <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Parts</legend>
          {choice(f('head'), 'Head', c.head.shape, CHARACTER_STYLES.head, (v) => set('Head', { head: { shape: v } }, 'head'))}
          {choice(f('eyes'), 'Eyes', c.eyes.style, CHARACTER_STYLES.eyes, (v) => set('Eyes', { eyes: { style: v } }, 'eyes'))}
          {choice(f('hair'), 'Hair', c.hair.style, CHARACTER_STYLES.hair, (v) => set('Hair', { hair: { style: v } }, 'hair'))}
          {choice(f('top'), 'Top', c.clothing.top, CHARACTER_STYLES.top, (v) => set('Clothing', { clothing: { top: v } }, 'top'))}
          {choice(f('bottom'), 'Bottom', c.clothing.bottom, CHARACTER_STYLES.bottom, (v) => set('Clothing', { clothing: { bottom: v } }, 'bottom'))}
        </fieldset>
        <fieldset className="grid grid-cols-2 gap-2 @lg:grid-cols-4">
          <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">Colours</legend>
          {color(f('skin'), 'Skin', c.skin, (v) => set('Colours', { skin: v }, 'skin'))}
          {color(f('eye-c'), 'Eyes', c.eyes.color, (v) => set('Colours', { eyes: { color: v } }, 'eyeColor'))}
          {color(f('hair-c'), 'Hair', c.hair.color, (v) => set('Colours', { hair: { color: v } }, 'hairColor'))}
          {color(f('top-c'), 'Top', c.clothing.topColor, (v) => set('Colours', { clothing: { topColor: v } }, 'topColor'))}
          {color(f('accent-c'), 'Accent', c.clothing.accentColor, (v) => set('Colours', { clothing: { accentColor: v } }, 'accentColor'))}
          {color(f('bottom-c'), 'Bottom', c.clothing.bottomColor, (v) => set('Colours', { clothing: { bottomColor: v } }, 'bottomColor'))}
          {color(f('shoe-c'), 'Shoes', c.clothing.shoeColor, (v) => set('Colours', { clothing: { shoeColor: v } }, 'shoeColor'))}
        </fieldset>
        <fieldset className="flex flex-col gap-1">
          <legend className="mb-1 text-[11px] font-semibold tracking-wide text-zinc-400 uppercase">
            Accessories ({c.accessories.length}/{MAX_ACCESSORIES})
          </legend>
          <div className="grid grid-cols-2 gap-1 @md:grid-cols-3">
            {CHARACTER_STYLES.accessory.map((kind) => {
              const a = accessory(kind);
              return (
                <div key={kind} className="flex items-center gap-1.5 text-sm">
                  <input id={f(`acc-${kind}`)} type="checkbox" checked={!!a} disabled={!a && c.accessories.length >= MAX_ACCESSORIES} onChange={(e) => toggleAccessory(kind, e.target.checked)} />
                  <label htmlFor={f(`acc-${kind}`)}>{label(kind)}</label>
                  {a && (
                    <input
                      type="color"
                      aria-label={`${label(kind)} colour`}
                      value={a.color}
                      onChange={(e) => set('Accessory colour', { accessories: c.accessories.map((x) => (x.kind === kind ? { ...x, color: e.target.value } : x)) }, `acc-${kind}`)}
                      className="h-6 w-8 shrink-0 cursor-pointer rounded border border-zinc-700 bg-transparent"
                    />
                  )}
                </div>
              );
            })}
          </div>
        </fieldset>
        </div>
      </div>
    </div>
  );
}
